import { FormProvider, useFieldArray } from 'react-hook-form'
import React, { useEffect } from 'react'
import { useIntl } from 'react-intl'
import * as yup from 'yup'
import FormRow from '../../components/formControls/FormRow'
import EmailAddressFieldList from './EmailAddressFieldList'
import useForm from '../../hooks/useForm'
import MultiSelect from '../../components/formControls/MultiSelect'
import { LabelValue } from '../../types/LabelValue'
import { SendDocumentsFormValues } from './types'
import LoadingButton from '../../components/Button/LoadingButton'

export type SendDocumentsFormProps = {
	documentIDs: string[]
	loanUserOptions: LabelValue[]
	loading: boolean
	onSubmit?: (data: SendDocumentsFormValues) => void
}

const emailSchema = yup.string().email().required()

const schema = yup.object().shape({
	documentIDs: yup.array().of(yup.string()).required(),
	loanUserIDs: yup.array().of(yup.string()),
	emailAddresses: yup.array().of(emailSchema),
})

const SendDocumentsForm = ({
	documentIDs,
	loanUserOptions,
	loading,
	onSubmit,
}: SendDocumentsFormProps) => {
	const { formatMessage } = useIntl()

	const methods = useForm<SendDocumentsFormValues>({
		defaultValues: {
			documentIDs,
			loanUserIDs: [],
			emailAddresses: [],
		},
		schema,
	})

	const { control, handleSubmit, watch, setValue } = methods

	const {
		fields: emailAddresses,
		append,
		remove,
	} = useFieldArray({
		control,
		name: 'emailAddresses' as never,
		rules: {
			required: true,
			validate: (values) => {
				return (
					Array.isArray(values) &&
					values.every((email) => emailSchema.isValidSync(email))
				)
			},
		},
	})

	const loanUserIdsWatch = watch('loanUserIDs')
	const emailAddressesWatch = watch('emailAddresses')

	const hasRecipients =
		loanUserIdsWatch.length > 0 ||
		emailAddressesWatch.some((e) => e && e.trim() !== '')

	const handleFormSubmit = (data: SendDocumentsFormValues) => {
		onSubmit?.(data)
	}

	useEffect(() => {
		setValue('documentIDs', documentIDs)
	}, [documentIDs, setValue])

	return (
		<FormProvider {...methods}>
			<FormRow>
				<fieldset>
					<legend className="block mb-3">
						{formatMessage({
							id: 'send_documents_form.loan_users_label',
						})}
						:
					</legend>
					<MultiSelect<LabelValue>
						name="loanUserIDs"
						menuItems={loanUserOptions}
						selectProps={{
							multiple: true,
						}}
						fullWidth
					/>
				</fieldset>
			</FormRow>
			<FormRow className="mt-4">
				<fieldset>
					<legend className="block mb-3">External emails:</legend>
					<EmailAddressFieldList
						emailAddresses={emailAddresses}
						onAddEmail={append}
						onRemoveEmail={remove}
					/>
				</fieldset>
			</FormRow>

			<FormRow className="flex justify-end">
				<LoadingButton
					text={formatMessage({ id: 'global.send' })}
					onClick={handleSubmit(handleFormSubmit)}
					disabled={loading || !hasRecipients}
					loading={loading}
					fullWidth={false}
				/>
			</FormRow>
		</FormProvider>
	)
}

export default SendDocumentsForm
